'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { createEngine } from '@/features/typing-engine';

/**
 * React hook that drives the typing engine with requestAnimationFrame.
 *
 * State machine: idle → running → finished (or paused → running).
 * The engine itself is authoritative; React state mirrors its metrics.
 *
 * @param {{ text: string, durationSeconds: number }} options
 */
export function useTypingTest({ text, durationSeconds }) {
  const engineRef = useRef(null);
  const rafRef = useRef(null);
  const startTimeRef = useRef(0);
  const lastFrameRef = useRef(0);

  const [phase, setPhase] = useState('idle'); // idle | running | paused | finished
  const [metrics, setMetrics] = useState({
    grossWpm: 0,
    netWpm: 0,
    accuracy: 100,
    correctChars: 0,
    incorrectChars: 0,
    totalChars: 0,
    backspaces: 0,
    elapsedSeconds: 0,
    progress: 0,
  });
  const [remainingSeconds, setRemainingSeconds] = useState(durationSeconds);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [wasAutoPaused, setWasAutoPaused] = useState(false);

  const stopLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const finishRef = useRef(null);

  const loop = useCallback(
    (now) => {
      const engine = engineRef.current;
      if (!engine) return;

      engine.type('', now); // no-op call to accumulate elapsed since last sample

      const m = engine.getMetrics();
      const remaining = Math.max(0, durationSeconds - m.elapsedSeconds);

      setMetrics(m);
      setRemainingSeconds(Math.ceil(remaining));
      setCurrentIndex(engine.position);

      if (m.finished || remaining <= 0) {
        engine.finish(now);
        const finalMetrics = engine.getMetrics();
        setMetrics(finalMetrics);
        setPhase('finished');
        stopLoop();
        finishRef.current?.(finalMetrics);
        return;
      }

      lastFrameRef.current = now;
      rafRef.current = requestAnimationFrame(loop);
    },
    [durationSeconds, stopLoop]
  );

  const start = useCallback(() => {
    engineRef.current = createEngine(text);
    startTimeRef.current = performance.now();
    lastFrameRef.current = startTimeRef.current;
    setWasAutoPaused(false);
    setPhase('running');
    setRemainingSeconds(durationSeconds);
    rafRef.current = requestAnimationFrame(loop);
  }, [text, durationSeconds, loop]);

  const pause = useCallback(() => {
    if (phase !== 'running') return;
    const now = performance.now();
    engineRef.current?.pause(now);
    stopLoop();
    setPhase('paused');
  }, [phase, stopLoop]);

  const resume = useCallback(() => {
    if (phase !== 'paused') return;
    const now = performance.now();
    engineRef.current?.resume(now);
    setWasAutoPaused(false);
    setPhase('running');
    rafRef.current = requestAnimationFrame(loop);
  }, [phase, loop]);

  const finish = useCallback(() => {
    if (phase !== 'running' && phase !== 'paused') return;
    const now = performance.now();
    engineRef.current?.finish(now);
    const m = engineRef.current?.getMetrics();
    if (m) setMetrics(m);
    setPhase('finished');
    stopLoop();
    if (m) finishRef.current?.(m);
  }, [phase, stopLoop]);

  const restart = useCallback(() => {
    stopLoop();
    setPhase('idle');
    setMetrics({
      grossWpm: 0,
      netWpm: 0,
      accuracy: 100,
      correctChars: 0,
      incorrectChars: 0,
      totalChars: 0,
      backspaces: 0,
      elapsedSeconds: 0,
      progress: 0,
    });
    setRemainingSeconds(durationSeconds);
    setCurrentIndex(0);
    setWasAutoPaused(false);
    engineRef.current = null;
  }, [durationSeconds, stopLoop]);

  const handleInput = useCallback(
    (input) => {
      if (phase !== 'running' || !engineRef.current) return;
      engineRef.current.type(input, performance.now());
      // Metrics will update on next frame.
    },
    [phase]
  );

  const handleBackspace = useCallback(() => {
    if (phase !== 'running' || !engineRef.current) return;
    engineRef.current.backspace();
  }, [phase]);

  // Auto-pause when tab is hidden.
  useEffect(() => {
    if (phase !== 'running') return;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        engineRef.current?.pause(performance.now());
        stopLoop();
        setPhase('paused');
        setWasAutoPaused(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [phase, stopLoop]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      stopLoop();
    };
  }, [stopLoop]);

  // Prevents accidental navigation during an active test.
  useEffect(() => {
    if (phase !== 'running') return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [phase]);

  return {
    phase,
    metrics,
    remainingSeconds,
    currentIndex,
    wasAutoPaused,
    setFinishCallback: (fn) => {
      finishRef.current = fn;
    },
    start,
    pause,
    resume,
    finish,
    restart,
    handleInput,
    handleBackspace,
    expectedChars: engineRef.current?.expectedChars || Array.from(text),
    finished: phase === 'finished',
  };
}